import {DoppeViewerAppClientServiceBase} from './doppe-viewer-app-client-service-base';
import {suspenseAsyncValueCreateWithParams} from '@wix/devzai-utils-react';
import {hashFunctionCreate, stringIsNotNullableOrWhiteSpace} from '@wix/devzai-utils-common';
import {WixAppsIds, WixBlogApiPostInfo, WixStoresApiProductInfo} from '@wix/devzai-common-wix';
import {
    doppeViewerAsyncListFactoryBlogPosts,
    doppeViewerAsyncListFactoryEvents,
    doppeViewerAsyncListFactoryProducts,
    doppeViewerAsyncListFactoryServices
} from "../../components/doppe-viewer-async-list-factories";

export class DoppeViewerDataCacheService extends DoppeViewerAppClientServiceBase {

    /**
     * Suspense cache for blog post data by slug.
     * Returns the blog post if found, or null if not found or no slug provided.
     */
    public readonly blogPostBySlug = suspenseAsyncValueCreateWithParams(
        hashFunctionCreate({
            blogPostSlug: hashFunctionCreate<string>(),
        }),
        async (params: {
            blogPostSlug: string;
        }): Promise<WixBlogApiPostInfo | null> => {
            const {blogPostSlug} = params;
            
            // If we have a slug, fetch the real blog post
            if (stringIsNotNullableOrWhiteSpace(blogPostSlug)) {
                const postInfo = await this.apiClient.getBlogPost({slug: blogPostSlug});
                if (postInfo) {
                    return postInfo;
                }
            }

            // No slug or not found - return null
            return null;
        }
    );

    /**
     * Suspense cache for store product data by slug.
     * Returns the product if found, or null if not found or no slug provided.
     * Shared by both physical and digital product renderers.
     */
    public readonly storeProductBySlug = suspenseAsyncValueCreateWithParams(
        hashFunctionCreate({
            productSlug: hashFunctionCreate<string>(),
        }),
        async (params: {
            productSlug: string;
        }): Promise<WixStoresApiProductInfo | null> => {
            const {productSlug} = params;
            
            // If we have a slug, fetch the real product
            if (stringIsNotNullableOrWhiteSpace(productSlug)) {
                const productInfo = await this.apiClient.getStoreProductInfo({slug: productSlug});
                if (productInfo) {
                    return productInfo;
                }
            }

            // No slug or not found - return null
            return null;
        }
    );

    public readonly cachedStoreProductsList = doppeViewerAsyncListFactoryProducts(() => this.apiClient);

    public readonly cachedBlogPostsList = doppeViewerAsyncListFactoryBlogPosts(() => this.apiClient);

    public readonly cachedEventsList = doppeViewerAsyncListFactoryEvents(() => this.apiClient);

    public readonly cachedServicesList = doppeViewerAsyncListFactoryServices(() => this.apiClient);

    public invalidateCreatorsMetaSiteWixTpaData (appDefId: string) {
        switch (appDefId) {
            case WixAppsIds.Stores:
            case WixAppsIds.StoresNew: {
                this.cachedStoreProductsList.invalidateAll();
                break;
            }
            case WixAppsIds.Blog: {
                this.cachedBlogPostsList.invalidateAll();
                break;
            }
            case WixAppsIds.Events: {
                this.cachedEventsList.invalidateAll();
                break;
            }
            case WixAppsIds.Booking: {
                this.cachedServicesList.invalidateAll();
                break;
            }
        }
    }
}
